export interface WidgetConfig {
  disclaimer: string;
  suggestedQuestions: string[];
  brandColor: string;
  businessHours: string;
  greeting: string;
  turnstileSiteKey: string;
  widgetVersion: string;
}

export interface ChatChunk {
  type: 'text' | 'meta' | 'error';
  text?: string;
  meta?: {
    conversationId: string;
    messageId: string;
    escalate: boolean;
    terminal?: boolean;
    reason?: string;
    handoff?: boolean;
    handoffSummary?: string;
  };
  error?: string;
}

export async function getWidgetConfig(apiBase: string): Promise<WidgetConfig> {
  const res = await fetch(`${apiBase}/api/widget/config`);
  if (!res.ok) throw new Error(`widget config fetch failed: ${res.status}`);
  return (await res.json()) as WidgetConfig;
}

export interface EscalateInput {
  conversationId: string;
  email: string;
  name?: string;
  phone?: string;
  message?: string;
}

export type EscalateResult =
  | { kind: 'ok'; ticketId: string; ticketCode?: string }
  | { kind: 'rate_limited' }
  | { kind: 'already_escalated'; ticketId?: string; ticketCode?: string }
  | { kind: 'error'; reason?: string };

export async function postEscalate(
  apiBase: string,
  input: EscalateInput
): Promise<EscalateResult> {
  try {
    const res = await fetch(`${apiBase}/api/escalate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        conversationId: input.conversationId,
        email: input.email,
        name: input.name || undefined,
        phone: input.phone || undefined,
        message: input.message || undefined
      })
    });
    if (res.status === 201) {
      const body = (await res.json()) as { ticketId: string; ticketCode?: string };
      return { kind: 'ok', ticketId: body.ticketId, ticketCode: body.ticketCode };
    }
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as { ticketId?: string; ticketCode?: string };
      return { kind: 'already_escalated', ticketId: body.ticketId, ticketCode: body.ticketCode };
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { kind: 'error', reason: body.error };
  } catch {
    return { kind: 'error' };
  }
}

export async function postHandoff(
  apiBase: string,
  body: { conversationId: string; email: string; summary?: string }
): Promise<
  | { kind: 'live'; sessionId: string }
  | { kind: 'ticket'; ticketId: string; ticketCode?: string }
  | { kind: 'error' }
> {
  try {
    const res = await fetch(`${apiBase}/api/handoff`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (res.status === 201 || res.status === 200) {
      const b = (await res.json()) as { mode: 'live' | 'ticket'; sessionId?: string; ticketId?: string; ticketCode?: string };
      return b.mode === 'live'
        ? { kind: 'live', sessionId: b.sessionId! }
        : { kind: 'ticket', ticketId: b.ticketId!, ticketCode: b.ticketCode };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export interface ChatHistory {
  conversationId: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string; createdAt: string }>;
  terminal: boolean;
  reason?: string;
}

export type ChatHistoryResult =
  | { kind: 'ok'; history: ChatHistory }
  | { kind: 'not_found' }
  | { kind: 'error' };

export async function getChatHistory(
  apiBase: string,
  conversationId: string
): Promise<ChatHistoryResult> {
  try {
    const res = await fetch(
      `${apiBase}/api/chat/messages?conversationId=${encodeURIComponent(conversationId)}`
    );
    if (res.status === 404) return { kind: 'not_found' };
    if (!res.ok) return { kind: 'error' };
    const history = (await res.json()) as ChatHistory;
    return { kind: 'ok', history };
  } catch {
    return { kind: 'error' };
  }
}

export interface LiveStateMessage {
  role: 'user' | 'assistant';
  content: string;
  agentName: string | null;
  createdAt: string;
}

export interface LiveState {
  status: 'queued' | 'active' | 'ended' | 'expired';
  agentName: string | null;
  endedReason: string | null;
  ticketCode: string | null;
  messages: LiveStateMessage[];
}

export async function getLiveAvailability(apiBase: string): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase}/api/widget/availability`);
    if (!res.ok) return false;
    const body = (await res.json()) as { liveChatAvailable: boolean };
    return body.liveChatAvailable;
  } catch {
    return false;
  }
}

export async function getLiveState(
  apiBase: string,
  conversationId: string,
  cursor?: string
): Promise<LiveState | null> {
  try {
    const qs = new URLSearchParams({ conversationId });
    if (cursor) qs.set('cursor', cursor);
    const res = await fetch(`${apiBase}/api/chat/live/state?${qs.toString()}`);
    if (!res.ok) return null;
    return (await res.json()) as LiveState;
  } catch {
    return null;
  }
}

export async function sendLiveMessage(
  apiBase: string,
  conversationId: string,
  message: string
): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase}/api/chat/live/message`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ conversationId, message })
    });
    return res.status === 201;
  } catch {
    return false;
  }
}

export async function* postChat(
  apiBase: string,
  body: {
    conversationId?: string;
    message: string;
    pageUrl?: string;
    turnstileToken?: string;
  }
): AsyncGenerator<ChatChunk> {
  const res = await fetch(`${apiBase}/api/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    let errBody: { error?: string; reason?: string } = {};
    try {
      errBody = (await res.json()) as { error?: string; reason?: string };
    } catch {
      /* ignore */
    }
    yield { type: 'error', error: errBody.error ?? `http_${res.status}` };
    return;
  }

  if (!res.body) {
    yield { type: 'error', error: 'no_body' };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl = buf.indexOf('\n');
    while (nl !== -1) {
      const line = buf.slice(0, nl);
      buf = buf.slice(nl + 1);
      if (line.startsWith('0:')) {
        try {
          yield { type: 'text', text: JSON.parse(line.slice(2)) as string };
        } catch {
          /* malformed text frame */
        }
      } else if (line.startsWith('2:')) {
        // Vercel AI SDK Data Stream Protocol: '2:' frames carry custom data
        // appended via StreamData.append(value). The SDK wraps the value in
        // an array, so the payload is `[meta]`. We treat the first element
        // as the meta frame (synthetic streams in route.ts use the same
        // wire shape).
        try {
          const arr = JSON.parse(line.slice(2)) as unknown;
          if (Array.isArray(arr) && arr.length > 0) {
            yield { type: 'meta', meta: arr[0] as ChatChunk['meta'] };
          }
        } catch {
          /* malformed data frame */
        }
      }
      nl = buf.indexOf('\n');
    }
  }
}

/**
 * Records a thumbs rating on a bot turn (spec §7.3).
 *
 * Fire-and-forget from the caller's point of view: a rating is a courtesy
 * signal, and a failure here must never interrupt the conversation. The
 * boolean is only used to roll the button back to its unselected state.
 */
export async function postFeedback(
  apiBase: string,
  input: { messageId: string; rating: 1 | -1 }
): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase}/api/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ messageId: input.messageId, rating: input.rating })
    });
    return res.ok;
  } catch {
    return false;
  }
}
